import { useState, useEffect, useMemo } from 'react';
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, Timestamp, addDoc, deleteDoc, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import SubmitRequestModal from '../components/SubmitRequestModal';
import RequestDetailModal from '../components/RequestDetailModal';
import ProfileModal from '../components/ProfileModal';
import { Loader2, Plus, TrendingUp, MessageSquare, ExternalLink, ChevronUp, Calendar, Sparkles, Search, Filter, SlidersHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { getNicknameInitials } from '../utils/nicknameGenerator';

// Helper function to format status for display
const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to get status badge colors
const getStatusColors = (status: string) => {
  switch (status.toLowerCase()) {
    case 'in_progress':
      return {
        bg: 'rgba(59, 130, 246, 0.1)', // Blue background
        text: 'rgb(37, 99, 235)', // Blue text
        border: 'rgba(59, 130, 246, 0.3)', // Blue border
      };
    case 'completed':
      return {
        bg: 'rgba(34, 197, 94, 0.1)', // Green background
        text: 'rgb(22, 163, 74)', // Green text
        border: 'rgba(34, 197, 94, 0.3)', // Green border
      };
    case 'open':
      return {
        bg: 'rgba(148, 163, 184, 0.1)', // Gray/slate background
        text: 'rgb(100, 116, 139)', // Gray/slate text
        border: 'rgba(148, 163, 184, 0.3)', // Gray/slate border
      };
    default:
      return {
        bg: 'rgba(148, 163, 184, 0.1)', // Default gray
        text: 'rgb(100, 116, 139)',
        border: 'rgba(148, 163, 184, 0.3)',
      };
  }
};

interface Customer {
  id: string;
  companyName: string;
  slug: string;
  domain: string;
  brandColor: string;
  accentColor?: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
}

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  createdBy: string;
  createdByEmail: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export default function VotingPortal() {
  const { slug } = useParams<{ slug: string }>();
  const { user, userProfile } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'votes' | 'recent' | 'status'>('votes');

  // Filter and sort requests based on current state
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...requests];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(req =>
        req.title.toLowerCase().includes(query) ||
        req.description.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(req => req.category === categoryFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'votes') {
        // Sort by votes (descending), then by date (descending)
        if (b.voteCount !== a.voteCount) {
          return b.voteCount - a.voteCount;
        }
        return b.createdAt.toMillis() - a.createdAt.toMillis();
      } else if (sortBy === 'recent') {
        // Sort by date (most recent first)
        return b.createdAt.toMillis() - a.createdAt.toMillis();
      } else {
        // Sort by status priority, then by votes
        const statusOrder: { [key: string]: number } = {
          'in_progress': 1,
          'open': 2,
          'completed': 3,
          'wont_build': 4,
        };
        const statusA = statusOrder[a.status] || 999;
        const statusB = statusOrder[b.status] || 999;
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        return b.voteCount - a.voteCount;
      }
    });

    return filtered;
  }, [requests, searchQuery, statusFilter, categoryFilter, sortBy]);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!slug) {
        setError('No customer slug provided');
        setLoading(false);
        return;
      }

      try {
        const customersRef = collection(db, 'customers');
        const q = query(customersRef, where('slug', '==', slug));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setError('Customer not found');
          setLoading(false);
          return;
        }

        const customerData = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data(),
        } as Customer;

        setCustomer(customerData);

        // Update page title and favicon
        document.title = `${customerData.companyName} - Feature Requests`;
        if (customerData.faviconUrl) {
          const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
          if (favicon) {
            favicon.href = customerData.faviconUrl;
          }
        }
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError('Failed to load customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [slug]);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!customer) return;

      setLoadingRequests(true);
      try {
        const requestsRef = collection(db, 'customers', customer.id, 'requests');
        const q = query(requestsRef, orderBy('voteCount', 'desc'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);

        const requestsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as FeatureRequest[];

        setRequests(requestsList);
      } catch (err) {
        console.error('Error fetching requests:', err);
      } finally {
        setLoadingRequests(false);
      }
    };

    fetchRequests();
  }, [customer]);

  // Auto-open request from URL hash (e.g., #request-abc123)
  useEffect(() => {
    if (!requests.length) return;

    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#request-')) {
        const requestId = hash.substring(9); // Remove '#request-'
        const request = requests.find(r => r.id === requestId);
        if (request) {
          setSelectedRequest(request);
          // Scroll to top when modal opens
          window.scrollTo(0, 0);
        }
      }
    };

    // Handle initial hash on page load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [requests]);

  const refreshRequests = async () => {
    if (!customer) return;

    try {
      const requestsRef = collection(db, 'customers', customer.id, 'requests');
      const q = query(requestsRef, orderBy('voteCount', 'desc'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const requestsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as FeatureRequest[];

      setRequests(requestsList);
    } catch (err) {
      console.error('Error refreshing requests:', err);
    }
  };

  // Fetch user's votes
  useEffect(() => {
    const fetchUserVotes = async () => {
      if (!user || !customer) return;

      try {
        const votesQuery = query(
          collection(db, 'customers', customer.id, 'requests'),
        );
        const requestsSnapshot = await getDocs(votesQuery);

        const votedRequestIds = new Set<string>();

        // Check each request for user's vote
        for (const requestDoc of requestsSnapshot.docs) {
          const votesRef = collection(db, 'customers', customer.id, 'requests', requestDoc.id, 'votes');
          const userVoteQuery = query(votesRef, where('userId', '==', user.uid));
          const voteSnapshot = await getDocs(userVoteQuery);

          if (!voteSnapshot.empty) {
            votedRequestIds.add(requestDoc.id);
          }
        }

        setUserVotes(votedRequestIds);
      } catch (err) {
        console.error('Error fetching user votes:', err);
      }
    };

    fetchUserVotes();
  }, [user, customer]);

  const handleVote = async (requestId: string) => {
    if (!user) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
      return;
    }

    if (!customer) return;

    const hasVoted = userVotes.has(requestId);

    try {
      if (hasVoted) {
        // Remove vote
        const votesRef = collection(db, 'customers', customer.id, 'requests', requestId, 'votes');
        const userVoteQuery = query(votesRef, where('userId', '==', user.uid));
        const voteSnapshot = await getDocs(userVoteQuery);

        if (!voteSnapshot.empty) {
          await deleteDoc(voteSnapshot.docs[0].ref);

          // Decrement vote count
          await updateDoc(doc(db, 'customers', customer.id, 'requests', requestId), {
            voteCount: increment(-1)
          });

          // Update local state
          setUserVotes(prev => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
          });

          setRequests(prev => prev.map(req =>
            req.id === requestId ? { ...req, voteCount: req.voteCount - 1 } : req
          ));
        }
      } else {
        // Add vote
        await addDoc(collection(db, 'customers', customer.id, 'requests', requestId, 'votes'), {
          userId: user.uid,
          userEmail: user.email || '',
          userNickname: userProfile?.nickname || '',
          customerId: customer.id,
          requestId: requestId,
          createdAt: Timestamp.now(),
        });

        // Increment vote count
        await updateDoc(doc(db, 'customers', customer.id, 'requests', requestId), {
          voteCount: increment(1)
        });

        // Update local state
        setUserVotes(prev => new Set(prev).add(requestId));

        setRequests(prev => prev.map(req =>
          req.id === requestId ? { ...req, voteCount: req.voteCount + 1 } : req
        ));
      }
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-destructive text-lg">{error || 'Customer not found'}</p>
          <p className="text-muted-foreground">
            The page you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  // Generate color variations for better brand consistency
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  };

  const hslToRgb = (h: number, s: number, l: number) => {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return {
      r: Math.round(255 * f(0)),
      g: Math.round(255 * f(8)),
      b: Math.round(255 * f(4)),
    };
  };

  const brandRgb = hexToRgb(customer.brandColor);
  const brandHsl = rgbToHsl(brandRgb.r, brandRgb.g, brandRgb.b);

  // Use customer's accent color if provided, otherwise generate complementary color
  let accentColor: string;
  let accentRgb: { r: number; g: number; b: number };

  if (customer.accentColor) {
    accentRgb = hexToRgb(customer.accentColor);
    accentColor = customer.accentColor;
  } else {
    // Generate complementary accent color as fallback
    const accentHsl = {
      h: (brandHsl.h + 150) % 360,
      s: Math.min(brandHsl.s * 0.9, 70),
      l: Math.min(brandHsl.l * 1.1, 50),
    };
    accentRgb = hslToRgb(accentHsl.h, accentHsl.s, accentHsl.l);
    accentColor = `rgb(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b})`;
  }

  const brandColorLight = `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.1)`;
  const brandColorMedium = `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.15)`;
  const brandColorSoft = `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.7)`;
  const accentColorLight = `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.1)`;
  const accentColorMedium = `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.15)`;

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{
        '--brand-color': customer.brandColor,
        '--brand-color-light': brandColorLight,
        '--brand-color-medium': brandColorMedium,
        '--brand-color-soft': brandColorSoft,
        '--accent-color': accentColor,
        '--accent-color-light': accentColorLight,
        '--accent-color-medium': accentColorMedium,
      } as React.CSSProperties}
    >
      {/* Header */}
      <header className="border-b bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {customer.logoUrl ? (
                <img
                  src={customer.logoUrl}
                  alt={`${customer.companyName} logo`}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div
                  className="h-10 w-10 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: customer.brandColor }}
                >
                  {customer.companyName.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-foreground">
                  {customer.companyName}
                </h1>
                <p className="text-xs text-muted-foreground">
                  Feature Requests & Feedback
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Live Indicator */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium text-green-600">Live</span>
              </div>

              {user && userProfile && (
                <button
                  onClick={() => setShowProfileModal(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:border-primary transition-colors"
                  title="View your profile"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: customer.brandColor }}
                  >
                    {getNicknameInitials(userProfile.nickname)}
                  </div>
                  <span className="text-sm font-medium text-foreground hidden sm:block">
                    {userProfile.nickname}
                  </span>
                </button>
              )}
              <a
                href={`https://${customer.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm transition-colors flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden sm:inline">Visit Website</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Full-Width Banner Section */}
      {(customer.bannerTitle || customer.bannerDescription) && (
        <div
          className="w-full py-16 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg,
              ${customer.brandColor}08 0%,
              ${customer.brandColor}20 25%,
              ${customer.brandColor}25 50%,
              ${customer.brandColor}20 75%,
              ${customer.brandColor}08 100%)`,
          }}
        >
          {/* Decorative gradient orbs */}
          <div
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-30 blur-3xl"
            style={{
              background: `radial-gradient(circle, ${customer.brandColor}40, transparent 70%)`,
            }}
          />
          <div
            className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-20 blur-3xl"
            style={{
              background: `radial-gradient(circle, ${accentColor || customer.brandColor}30, transparent 70%)`,
            }}
          />
          <div className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Badge */}
            <div className="mb-6 flex justify-center">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium relative overflow-hidden shadow-sm"
                style={{
                  backgroundColor: 'white',
                  color: customer.brandColor,
                }}
              >
                <Sparkles className="w-4 h-4" />
                <span
                  style={{
                    background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Shape the Future Together
                </span>
              </div>
            </div>

            {/* Title with styled word */}
            {customer.bannerTitle && (
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                {customer.bannerTitle.split(' ').map((word, index, arr) => {
                  const isLastWord = index === arr.length - 1;
                  return (
                    <span
                      key={index}
                      style={
                        isLastWord
                          ? {
                              background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }
                          : {}
                      }
                      className={isLastWord ? '' : 'text-gray-900'}
                    >
                      {word}{index < arr.length - 1 ? ' ' : ''}
                    </span>
                  );
                })}
              </h2>
            )}
            {customer.bannerDescription && (
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
                {customer.bannerDescription}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">

          {/* Header with Submit Button */}
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Feature Requests
              </h2>
              <p className="text-gray-600 text-base">
                Vote on features you'd like to see, or suggest your own
              </p>
            </div>
            <Button
              size="lg"
              style={{
                background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                boxShadow: `0 4px 12px ${brandColorMedium}`,
              }}
              className="text-white hover:opacity-90 transition-all hover:shadow-lg shrink-0"
              onClick={() => setShowSubmitModal(true)}
            >
              <Plus className="h-5 w-5 mr-2" />
              Submit Request
            </Button>
          </div>

          {/* Search, Filter, and Sort Controls */}
          {requests.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search Input */}
                <div className="relative lg:col-span-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search requests..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      <SelectValue placeholder="All Statuses" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>

                {/* Sort Dropdown */}
                <Select value={sortBy} onValueChange={(value: string) => setSortBy(value as 'votes' | 'recent' | 'status')}>
                  <SelectTrigger className="w-full">
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4" />
                      <SelectValue placeholder="Sort by" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="votes">Most Votes</SelectItem>
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="status">By Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter Pills */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-700 flex items-center">
                  Category:
                </span>
                <button
                  onClick={() => setCategoryFilter('all')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === 'all'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={categoryFilter === 'all' ? { backgroundColor: customer.brandColor } : undefined}
                >
                  All
                </button>
                <button
                  onClick={() => setCategoryFilter('feature')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === 'feature'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={categoryFilter === 'feature' ? { backgroundColor: customer.brandColor } : undefined}
                >
                  Feature
                </button>
                <button
                  onClick={() => setCategoryFilter('improvement')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === 'improvement'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={categoryFilter === 'improvement' ? { backgroundColor: customer.brandColor } : undefined}
                >
                  Improvement
                </button>
                <button
                  onClick={() => setCategoryFilter('bug_fix')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === 'bug_fix'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={categoryFilter === 'bug_fix' ? { backgroundColor: customer.brandColor } : undefined}
                >
                  Bug Fix
                </button>
                <button
                  onClick={() => setCategoryFilter('other')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === 'other'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={categoryFilter === 'other' ? { backgroundColor: customer.brandColor } : undefined}
                >
                  Other
                </button>
              </div>

              {/* Results Count */}
              {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-semibold">{filteredAndSortedRequests.length}</span> of <span className="font-semibold">{requests.length}</span> requests
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Requests List or Empty State */}
          {loadingRequests ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading requests...</p>
            </div>
          ) : filteredAndSortedRequests.length === 0 && requests.length > 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <div className="flex justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center border-2"
                    style={{
                      backgroundColor: brandColorLight,
                      borderColor: brandColorMedium,
                    }}
                  >
                    <Search
                      className="h-8 w-8"
                      style={{ color: customer.brandColor }}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    No requests found
                  </h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search or filters
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setCategoryFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <div className="flex justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center border-2"
                    style={{
                      backgroundColor: brandColorLight,
                      borderColor: brandColorMedium,
                    }}
                  >
                    <TrendingUp
                      className="h-8 w-8"
                      style={{ color: customer.brandColor }}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    No feature requests yet
                  </h3>
                  <p className="text-muted-foreground">
                    Be the first to suggest a feature or improvement!
                  </p>
                </div>
                <Button
                  size="lg"
                  style={{
                    backgroundColor: customer.brandColor,
                    boxShadow: `0 4px 12px ${brandColorMedium}`,
                  }}
                  className="text-white hover:opacity-90 mt-4 transition-all hover:shadow-lg"
                  onClick={() => setShowSubmitModal(true)}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Submit the First Request
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAndSortedRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-all hover:border-gray-300"
                >
                  <div className="flex gap-4">
                    {/* Vote Button */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(request.id);
                        }}
                        className={`w-12 h-12 rounded-md border flex items-center justify-center transition-all hover:scale-105 ${
                          userVotes.has(request.id)
                            ? ''
                            : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400 hover:bg-gray-100'
                        }`}
                        style={
                          userVotes.has(request.id)
                            ? {
                                backgroundColor: customer.brandColor,
                                borderColor: customer.brandColor,
                                color: 'white',
                              }
                            : undefined
                        }
                      >
                        <ChevronUp className="h-5 w-5" />
                      </button>
                      <span
                        className="text-sm font-bold"
                        style={{
                          color: userVotes.has(request.id)
                            ? customer.brandColor
                            : '#64748b',
                        }}
                      >
                        {request.voteCount}
                      </span>
                    </div>

                    {/* Request Content */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setSelectedRequest(request)}
                    >
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-semibold text-foreground hover:text-primary transition-colors">
                          {request.title}
                        </h3>
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border"
                          style={{
                            backgroundColor: getStatusColors(request.status).bg,
                            color: getStatusColors(request.status).text,
                            borderColor: getStatusColors(request.status).border,
                          }}
                        >
                          {formatStatus(request.status)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {request.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="px-2.5 py-1 rounded-md capitalize font-medium bg-blue-50 text-blue-600 border border-blue-200">
                          {request.category.replace('_', ' ')}
                        </span>
                        <span className="flex items-center gap-1 text-gray-500">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(request.createdAt.toDate()).toLocaleDateString()}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRequest(request);
                          }}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors font-medium ml-auto"
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          View details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* How It Works Section */}
          <div className="py-16 mt-8">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                How It Works
              </h3>
              <p className="text-gray-600 text-base max-w-2xl mx-auto">
                Our feedback process is simple and transparent. Here's how you can help shape our product roadmap.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Step 1 */}
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="mb-5 flex justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                    }}
                  >
                    <MessageSquare className="h-7 w-7 text-white" />
                  </div>
                </div>
                <div className="mb-4">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: customer.brandColor }}
                  >
                    1
                  </span>
                  <span className="text-lg font-bold text-gray-900 ml-2">
                    Submit Requests
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Share your ideas and feature requests. Tell us what would make the product better for you.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="mb-5 flex justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                    }}
                  >
                    <ChevronUp className="h-7 w-7 text-white" />
                  </div>
                </div>
                <div className="mb-4">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: customer.brandColor }}
                  >
                    2
                  </span>
                  <span className="text-lg font-bold text-gray-900 ml-2">
                    Vote & Discuss
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Vote on requests from other users to show support. Join the conversation with comments.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="mb-5 flex justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                    }}
                  >
                    <TrendingUp className="h-7 w-7 text-white" />
                  </div>
                </div>
                <div className="mb-4">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: customer.brandColor }}
                  >
                    3
                  </span>
                  <span className="text-lg font-bold text-gray-900 ml-2">
                    We Prioritize
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  We review all feedback and prioritize based on votes, impact, and feasibility.
                </p>
              </div>

              {/* Step 4 */}
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="mb-5 flex justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor || customer.brandColor} 0%, ${customer.brandColor} 100%)`,
                    }}
                  >
                    <Loader2 className="h-7 w-7 text-white" />
                  </div>
                </div>
                <div className="mb-4">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: customer.brandColor }}
                  >
                    4
                  </span>
                  <span className="text-lg font-bold text-gray-900 ml-2">
                    Track Progress
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Follow the status of requests as they move from open to in progress to completed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8 py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <p className="text-gray-600">
              Powered by{' '}
              <a
                href={import.meta.env.VITE_MARKETING_URL || 'https://getproductworkshop.com'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 transition-colors font-medium"
              >
                ProductWorkshop
              </a>
            </p>
            <div className="flex items-center gap-6">
              <Link
                to={`/${customer.slug}/admin`}
                className="text-blue-600 hover:text-blue-700 transition-colors"
              >
                Admin Portal
              </Link>
              <a
                href="#"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Documentation
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Submit Request Modal */}
      {showSubmitModal && customer && (
        <SubmitRequestModal
          customerId={customer.id}
          customerName={customer.companyName}
          brandColor={customer.brandColor}
          onClose={() => setShowSubmitModal(false)}
          onSuccess={refreshRequests}
        />
      )}

      {/* Request Detail Modal */}
      {selectedRequest && customer && (
        <RequestDetailModal
          request={selectedRequest}
          customerId={customer.id}
          brandColor={customer.brandColor}
          hasVoted={userVotes.has(selectedRequest.id)}
          onClose={() => setSelectedRequest(null)}
          onVote={handleVote}
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && customer && (
        <ProfileModal
          brandColor={customer.brandColor}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}
